/**
 * Shared marketing primitives — server-component-friendly visual atoms
 * ported from `marketing_v2/shared.jsx`.
 *
 * Design tokens come from `src/styles/design-tokens.css` (`--color-*`,
 * `--font-sans`, `--font-mono`) scoped to `[data-marketing]` wrappers.
 * Interactive pieces (Btn hover, Reveal animation) live in separate
 * `"use client"` files to keep these primitives RSC-safe.
 */

import { type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

// ─── Container ──────────────────────────────────────────────────────────

interface ContainerProps {
  children: ReactNode;
  max?: number;
  className?: string;
  style?: CSSProperties;
}

export function Container({
  children,
  max = 1280,
  className,
  style,
}: ContainerProps) {
  return (
    <div
      className={cn("mx-auto px-8", className)}
      style={{ maxWidth: max, ...style }}
    >
      {children}
    </div>
  );
}

// ─── Eyebrow ────────────────────────────────────────────────────────────

interface EyebrowProps {
  children: ReactNode;
  color?: string;
  className?: string;
}

export function Eyebrow({ children, color, className }: EyebrowProps) {
  return (
    <div
      className={cn(
        "text-[11px] uppercase tracking-[0.14em]",
        className,
      )}
      style={{
        fontFamily: "var(--font-mono)",
        color: color ?? "var(--color-blue)",
      }}
    >
      {children}
    </div>
  );
}

// ─── SectionHead ────────────────────────────────────────────────────────

interface SectionHeadProps {
  eyebrow?: string;
  eyebrowColor?: string;
  title: ReactNode;
  sub?: ReactNode;
  align?: "left" | "center";
  className?: string;
}

export function SectionHead({
  eyebrow,
  eyebrowColor,
  title,
  sub,
  align = "left",
  className,
}: SectionHeadProps) {
  return (
    <div
      className={cn(
        align === "center" ? "text-center" : "text-left",
        className,
      )}
    >
      {eyebrow ? (
        <Eyebrow color={eyebrowColor} className="mb-3">
          {eyebrow}
        </Eyebrow>
      ) : null}
      <h2
        className="text-[clamp(32px,5vw,48px)] font-semibold leading-[1.05] tracking-[-0.025em]"
        style={{ color: "var(--color-text)" }}
      >
        {title}
      </h2>
      {sub ? (
        <p
          className="mt-4 max-w-[640px] text-[18px] leading-[1.55]"
          style={{
            color: "var(--color-muted)",
            ...(align === "center"
              ? { marginLeft: "auto", marginRight: "auto" }
              : {}),
          }}
        >
          {sub}
        </p>
      ) : null}
    </div>
  );
}

// ─── Hairline ───────────────────────────────────────────────────────────

interface HairlineProps {
  label?: string;
  className?: string;
}

export function Hairline({ label, className }: HairlineProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-4 text-[11px] uppercase tracking-[0.14em]",
        className,
      )}
      style={{
        fontFamily: "var(--font-mono)",
        color: "var(--color-dim)",
      }}
    >
      <div
        className="h-px flex-1"
        style={{ background: "var(--color-line)" }}
      />
      {label ? <span>{label}</span> : null}
      <div
        className="h-px flex-1"
        style={{ background: "var(--color-line)" }}
      />
    </div>
  );
}

// ─── Logo ───────────────────────────────────────────────────────────────

interface LogoProps {
  size?: number;
  className?: string;
}

/**
 * Placeholder WTM logo — geometric mark from `shared.jsx`. Dispatch open
 * item: verify with Anuj if this is final or needs refinement.
 */
export function Logo({ size = 24, className }: LogoProps) {
  return (
    <div
      className={cn("inline-flex items-center gap-2 font-semibold", className)}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="4"
          stroke="var(--color-text)"
          strokeWidth="1.5"
        />
        <path
          d="M7 14L10 9L14 13L17 8"
          stroke="var(--color-blue-2)"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="text-[15px] tracking-[-0.01em]"
        style={{ color: "var(--color-text)" }}
      >
        WhatsTheMoat
      </span>
    </div>
  );
}
